/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useState } from "react";

import { isElectron } from "react-device-detect";

import { useCurrentUser } from "src/app/useCurrentUser";
import { BasePlate } from "src/classes/plate/BasePlate/BasePlate";
import { Rectangle } from "src/classes/plate/lib/types";
import { ConnectMenu } from "src/components/Shared/ConnectMenu";
import { Alert } from "src/components/UI/Alert";
import { Menu } from "src/components/UI/Menus/Menu";
import { MenuBreak } from "src/components/UI/Menus/MenuItem/MenuBreak";
import { MenuItem } from "src/components/UI/Menus/MenuItem/MenuItem";

interface Props {
  plate: BasePlate;
  children: React.ReactNode | React.ReactNode[];
}

export function ContextMenu({ plate, children }: Props) {
  const currentUser = useCurrentUser();
  const loggedIn = !!currentUser;
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);

  const isOwner = plate.observe((state) => state.currentUserIsOwner);
  const isLiked = plate.observe((state) => state.isLiked);
  const isPublic = plate.observe((state) => state.isPublic);

  const onDeleteAccept = async () => {
    setDeleteAlertOpen(false);
    await currentUser?.deletePlate(plate);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteAlertOpen(true);
  };
  // controls whether and where the menu is shown
  const [anchor, setAnchor] = useState<Rectangle | null>();

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchor({ top: e.pageY, right: e.pageX, bottom: e.pageY, left: e.pageX });
  };

  return (
    <>
      <div onContextMenu={handleContextMenu}>{children}</div>
      {anchor && (
        <Menu anchor={anchor} close={() => setAnchor(null)} blockMouseEvents={true}>
          <MenuItem condition={!isElectron} handleClick={plate.openInNewTab}>
            Open in New Tab
          </MenuItem>
          <MenuItem handleClick={plate.copyLink}>Copy Link</MenuItem>
          <MenuBreak />
          <MenuItem condition={loggedIn} handleClick={plate.like}>
            {isLiked ? "Unlike" : "Like"}
          </MenuItem>
          <MenuItem condition={isOwner} handleClick={() => plate.changePrivacy()}>
            {isPublic ? "Make Private" : "Make Public"}
          </MenuItem>
          <MenuItem
            condition={loggedIn}
            hasSubOptions
            hoverComponent={<ConnectMenu plate={plate} blockMouseEvents={false} block={null} />}
          >
            Add To Plate
          </MenuItem>
          <MenuBreak condition={isOwner} />
          <MenuItem condition={isOwner} handleClick={handleDelete}>
            Delete
          </MenuItem>

          <Alert
            type="question"
            acceptText="Yes"
            declineText="No"
            onAccept={onDeleteAccept}
            onDecline={() => setDeleteAlertOpen(false)}
            isOpen={deleteAlertOpen}
            headerText="Are you sure you want to delete this plate?"
            bodyText="Deleting is a permanent action and cannot be undone."
          />
        </Menu>
      )}
    </>
  );
}
